import { useSyncExternalStore } from "react";

/**
 * 1つのメディアクエリを、アプリ全体で1本のmatchMedia changeリスナーに集約する
 * モジュールスコープのシングルトンストアを生成するファクトリ。
 *
 * 引用行ごと（QuoteHoverPreview等）やレス数分マウントされるコンポーネントが、
 * 同じメディアクエリを各々matchMediaで購読するとリスナーが購読者数分だけ倍増し、
 * メディア条件が変化するたびに全購読者へchangeイベントが同時配送される
 * （Issue #50 / #16 レビュー指摘）。本ファクトリで生成したストアを共有すれば、
 * 実際のネイティブリスナーは購読者が1人以上いる間だけ1本しか登録しない。
 *
 * `useSystemPrefersDark`で確立した共有シングルトン方式を、任意のメディアクエリへ
 * 汎用化したもの。
 */
export interface SharedMediaQueryStore {
  /**
   * ストアのメディアクエリ一致状態を購読する。
   * @param enabled `false`の間は購読者に加わらず（no-op unsubscribe）、
   * 常に一定値を返してメディア条件変化を再レンダリング要因にしない。既定は`true`。
   */
  useMatches: (enabled?: boolean) => boolean;
  /** 現在の一致状態を購読なしで同期取得する（純粋な参照用）。 */
  getSnapshot: () => boolean;
}

export function createSharedMediaQueryStore(
  query: string,
): SharedMediaQueryStore {
  let mediaQueryList: MediaQueryList | null = null;
  let mediaQueryOwner: Window | null = null;
  let matchMediaSource: typeof window.matchMedia | null = null;
  const listeners: Set<() => void> = new Set();

  function getMediaQueryList(): MediaQueryList | null {
    if (typeof window === "undefined") return null;
    const currentWindow = window;
    const currentMatchMedia = currentWindow.matchMedia;
    if (
      mediaQueryList &&
      mediaQueryOwner === currentWindow &&
      matchMediaSource === currentMatchMedia
    ) {
      return mediaQueryList;
    }

    if (mediaQueryList && listeners.size > 0) {
      mediaQueryList.removeEventListener("change", handleChange);
    }
    mediaQueryList = currentMatchMedia.call(currentWindow, query);
    mediaQueryOwner = currentWindow;
    matchMediaSource = currentMatchMedia;
    if (listeners.size > 0) {
      mediaQueryList.addEventListener("change", handleChange);
    }
    return mediaQueryList;
  }

  function handleChange(): void {
    for (const listener of listeners) listener();
  }

  function subscribe(onStoreChange: () => void): () => void {
    const mql = getMediaQueryList();
    if (!mql) return (): void => {};

    if (listeners.size === 0) {
      mql.addEventListener("change", handleChange);
    }
    listeners.add(onStoreChange);

    return (): void => {
      listeners.delete(onStoreChange);
      if (listeners.size === 0) {
        mediaQueryList?.removeEventListener("change", handleChange);
      }
    };
  }

  /** enabled=falseの間はシングルトンの購読者に加わらない（no-op unsubscribe）。 */
  function disabledSubscribe(): () => void {
    return (): void => {};
  }

  function getSnapshot(): boolean {
    return getMediaQueryList()?.matches ?? false;
  }

  /** enabled=falseの間は常に一定値を返し、メディア条件変化を再レンダ要因にしない。 */
  function disabledGetSnapshot(): boolean {
    return false;
  }

  function getServerSnapshot(): boolean {
    return false;
  }

  function useMatches(enabled: boolean = true): boolean {
    return useSyncExternalStore(
      enabled ? subscribe : disabledSubscribe,
      enabled ? getSnapshot : disabledGetSnapshot,
      getServerSnapshot,
    );
  }

  return { useMatches, getSnapshot };
}
